import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Megaphone, Video, Target, PenTool, Globe, Compass, Rocket, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { TranslatedText } from '@/components/TranslatedText';
import { trackCTAClick } from '@/lib/metaPixel';

const tools = [
  {
    icon: Megaphone,
    title: "AI Ad Creative Generator",
    description: "Create scroll-stopping creatives for your Meta, TikTok and Google Ads. Pick a format, describe your offer, and download ready-to-launch visuals.",
    color: "from-rose-500/10 to-pink-500/10",
    iconColor: "text-rose-600 dark:text-rose-400",
  },
  {
    icon: Video,
    title: "AI Video Prompt Builder",
    description: "Generate the perfect prompt for your AI videos (Sora, Veo, Runway). Get cinematic shots, camera moves and pacing — ready to paste.",
    color: "from-blue-500/10 to-indigo-500/10",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  {
    icon: Target,
    title: "AI Marketing Plan",
    description: "Get a full marketing plan tailored to your offer: channels, budget split, funnel structure and weekly KPIs you can actually execute.",
    color: "from-purple-500/10 to-pink-500/10",
    iconColor: "text-purple-600 dark:text-purple-400",
  },
  {
    icon: PenTool,
    title: "AI Ad Copy & Quotes",
    description: "Generate ad descriptions, headlines, CTAs and marketing quotes that convert — optimized per platform and audience.",
    color: "from-amber-500/10 to-orange-500/10",
    iconColor: "text-amber-600 dark:text-amber-400",
  },
  {
    icon: Globe,
    title: "AI Website / Store / App Builder",
    description: "Describe your business and get a complete blueprint for your website, e-commerce store or app — structure, pages and copy included.",
    color: "from-emerald-500/10 to-teal-500/10",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  {
    icon: Compass,
    title: "AI Strategy & Niche Analyzer",
    description: "Validate your niche and get a full marketing strategy: positioning, competition, monetization paths and content angles that win.",
    color: "from-cyan-500/10 to-blue-500/10",
    iconColor: "text-cyan-600 dark:text-cyan-400",
  },
  {
    icon: Rocket,
    title: "AI Launch Strategy (7/14/30 Days)",
    description: "Get your day-by-day business launch plan for the first 7, 14 or 30 days — tasks, content, ads and milestones to hit revenue fast.",
    color: "from-fuchsia-500/10 to-purple-500/10",
    iconColor: "text-fuchsia-600 dark:text-fuchsia-400",
  },
];


export const LightAIToolsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="ai-tools" className="py-16 sm:py-24 px-4 bg-background">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            <TranslatedText>Built-In AI Tools — Exclusive to Members</TranslatedText>
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-3">
            <TranslatedText>Your Private AI Content Studio</TranslatedText>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            <TranslatedText>We built these tools from scratch so you never need third-party subscriptions. Generate images, scripts, captions, and strategies — all inside your dashboard.</TranslatedText>
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-lg mx-auto">
            <TranslatedText>Powered by our proprietary AI pipeline · No ChatGPT or Canva needed · Included with your membership</TranslatedText>
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-10">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
              className="group relative p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
            >
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl bg-muted flex items-center justify-center ${tool.iconColor}`}>
                    <tool.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    LIVE
                  </span>
                </div>
                <h3 className="text-base font-bold text-foreground mb-1.5">
                  <TranslatedText>{tool.title}</TranslatedText>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  <TranslatedText>{tool.description}</TranslatedText>
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Link to="/apply?plan=free" onClick={() => trackCTAClick('Try AI Tools Free', 'AIToolsSection')}>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 px-8 rounded-xl text-base font-semibold">
              <Sparkles className="w-4 h-4 mr-2" />
              <TranslatedText>Start Free — 10 Credits Included</TranslatedText>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-3">
            <TranslatedText>No credit card required · 10 free credits on signup</TranslatedText>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default LightAIToolsSection;
